import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { ArrowLeft, Clock, Linkedin, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import CTABanner from "../components/CTABanner";
import NewsletterSignup from "../components/NewsletterSignup";
import SuggestAnEdit from "../components/SuggestAnEdit";
import PageLoader from "../components/PageLoader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
};

/**
 * Lightweight markdown → HTML renderer.
 * Supports: # h1 (skipped — title comes from data), ## h2, ### h3, **bold**, lists, paragraphs, > blockquote.
 * Sufficient for AI-generated long-form article content.
 */
const renderMarkdown = (md) => {
  if (!md) return "";
  const lines = md.split("\n");
  const out = [];
  let inUl = false;
  let inOl = false;
  let para = [];

  const flushPara = () => {
    if (para.length) {
      out.push(`<p>${inline(para.join(" "))}</p>`);
      para = [];
    }
  };
  const closeLists = () => {
    if (inUl) {
      out.push("</ul>");
      inUl = false;
    }
    if (inOl) {
      out.push("</ol>");
      inOl = false;
    }
  };

  const inline = (s) =>
    s
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>");

  for (let raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushPara();
      closeLists();
      continue;
    }
    if (line.startsWith("### ")) {
      flushPara();
      closeLists();
      out.push(`<h3>${inline(line.slice(4))}</h3>`);
      continue;
    }
    if (line.startsWith("## ")) {
      flushPara();
      closeLists();
      out.push(`<h2>${inline(line.slice(3))}</h2>`);
      continue;
    }
    if (line.startsWith("# ")) {
      flushPara();
      closeLists();
      // Skip H1 — title is handled separately
      continue;
    }
    if (line.startsWith("> ")) {
      flushPara();
      closeLists();
      out.push(`<blockquote>${inline(line.slice(2))}</blockquote>`);
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      flushPara();
      if (inUl) {
        out.push("</ul>");
        inUl = false;
      }
      if (!inOl) {
        out.push("<ol>");
        inOl = true;
      }
      out.push(`<li>${inline(line.replace(/^\d+\.\s/, ""))}</li>`);
      continue;
    }
    if (line.startsWith("- ") || line.startsWith("* ")) {
      flushPara();
      if (inOl) {
        out.push("</ol>");
        inOl = false;
      }
      if (!inUl) {
        out.push("<ul>");
        inUl = true;
      }
      out.push(`<li>${inline(line.slice(2))}</li>`);
      continue;
    }
    para.push(line);
  }
  flushPara();
  closeLists();
  return out.join("\n");
};

const InsightArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    let alive = true;
    setLoading(true);
    axios
      .get(`${API}/insights/${slug}`)
      .then((res) => {
        if (alive) setArticle(res.data);
      })
      .catch((e) => {
        if (alive) setError(e?.response?.status === 404 ? "not_found" : "error");
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [slug]);

  // Article-level SEO: per-article <title>/description/canonical/OG + JSON-LD Article
  useEffect(() => {
    if (!article) return;
    const url = `https://www.ekselens.com/insights/${article.slug}`;
    document.title = `${article.title} | Ekselens Insights`;
    const setMeta = (sel, attrs) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        for (const [k, v] of Object.entries(attrs.create || {})) el.setAttribute(k, v);
        document.head.appendChild(el);
      }
      for (const [k, v] of Object.entries(attrs.set || {})) el.setAttribute(k, v);
    };
    setMeta('meta[name="description"]', { create: { name: "description" }, set: { content: article.summary || "" } });
    setMeta('meta[property="og:title"]', { create: { property: "og:title" }, set: { content: article.title } });
    setMeta('meta[property="og:description"]', { create: { property: "og:description" }, set: { content: article.summary || "" } });
    setMeta('meta[property="og:type"]', { create: { property: "og:type" }, set: { content: "article" } });
    setMeta('meta[property="og:url"]', { create: { property: "og:url" }, set: { content: url } });

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    let s = document.head.querySelector('script[data-jsonld="article"]');
    if (!s) {
      s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-jsonld", "article");
      document.head.appendChild(s);
    }
    s.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.summary,
      datePublished: article.published_at,
      author: { "@type": "Organization", name: "Ekselens Consulting" },
      publisher: {
        "@type": "Organization",
        name: "Ekselens Consulting",
        logo: { "@type": "ImageObject", url: "https://www.ekselens.com/ekselens-star.png" },
      },
      mainEntityOfPage: url,
      articleSection: article.category,
    });

    return () => {
      // leave the tags; SeoHead will reset them on route change
    };
  }, [article]);

  if (loading) {
    return (
      <PageLoader label="Loading article…" testid="article-loading" />
    );
  }

  if (error || !article) {
    return (
      <div className="ek-container py-32 text-center">
        <h1 className="ek-heading text-3xl text-black">Article not found</h1>
        <p className="ek-body mt-4">The article you're looking for is no longer available.</p>
        <Link to="/insights" className="ek-btn ek-btn-primary mt-8">Back to insights</Link>
      </div>
    );
  }

  return (
    <>
      <section className="ek-gradient-dark text-white relative overflow-hidden">
        <div className="ek-star-watermark" />
        <div className="ek-vignette-top" />
        <div className="ek-container relative z-10 py-16 md:py-20">
          <Link to="/insights" className="inline-flex items-center gap-2 text-white/90 text-sm font-medium hover:text-white">
            <ArrowLeft size={14} /> All insights
          </Link>
          <div className="mt-8">
            <div className="text-[11px] font-semibold tracking-[0.18em] text-[#A179FA] uppercase">
              {article.category}
            </div>
            <h1 className="ek-heading text-4xl md:text-5xl lg:text-6xl text-white mt-4 max-w-4xl leading-tight" data-testid="article-title">
              {article.title}
            </h1>
            <div className="mt-8 flex items-center gap-6 text-zinc-300 text-sm flex-wrap">
              <span>{formatDate(article.published_at)}</span>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={14} /> {article.reading_minutes} min read
              </span>
              <span className="inline-flex items-center gap-2">
                Ekselens Consulting
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="ek-container">
          <div className="max-w-3xl">
            <p className="ek-body text-xl text-black font-normal leading-relaxed mb-10">
              {article.summary}
            </p>
            <div
              className="ek-prose"
              // Insight body is admin-authored markdown rendered to HTML by
              // `renderMarkdown` above. We sanitize the output with DOMPurify
              // as defence-in-depth — restricts tags/attributes to a safe
              // subset so even a compromised admin account can't inject
              // <script>, on* handlers, or javascript: URLs.
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(renderMarkdown(article.body), {
                  ALLOWED_TAGS: [
                    "p", "br", "strong", "em", "ul", "ol", "li", "blockquote",
                    "h2", "h3", "h4", "a", "code", "pre",
                  ],
                  ALLOWED_ATTR: ["href", "title", "target", "rel"],
                }),
              }}
              data-testid="article-body"
            />

            {/* Share */}
            <div className="mt-14 pt-8 border-t border-zinc-200" data-testid="article-share">
              <div className="ek-eyebrow">Found this useful?</div>
              <p className="ek-body text-[14.5px] mt-3 max-w-xl">
                Sharing it on LinkedIn helps colleagues find it — and helps Ekselens reach more transformation leaders thinking about the same questions.
              </p>
              <div className="flex flex-wrap gap-3 mt-5">
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://www.ekselens.com/insights/${article.slug}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ek-btn ek-btn-primary text-[13px] px-5 py-2.5"
                  data-testid="share-linkedin"
                >
                  <Linkedin size={14} /> Share on LinkedIn
                </a>
                <button
                  type="button"
                  onClick={() => {
                    const url = `https://www.ekselens.com/insights/${article.slug}`;
                    if (navigator.clipboard?.writeText) {
                      navigator.clipboard
                        .writeText(url)
                        .then(() => toast.success("Article link copied"))
                        .catch(() => toast.error("Couldn't copy — try right-click"));
                    }
                  }}
                  className="ek-btn ek-btn-outline-dark text-[13px] px-5 py-2.5"
                  data-testid="share-copy-link"
                >
                  <LinkIcon size={14} /> Copy link
                </button>
              </div>
              <div className="mt-6">
                <SuggestAnEdit
                  articleSlug={`insights/${article.slug}`}
                  articleTitle={article.title}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <NewsletterSignup />
      <CTABanner />
    </>
  );
};

export default InsightArticle;
